import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const COLORS = ['#0ea5e9','#6366f1','#22c55e','#f59e0b','#ef4444','#a855f7','#14b8a6','#f97316','#ec4899','#64748b'];

const RANGES = [
  { label: 'Today',  value: 'today' },
  { label: 'Week',   value: 'week'  },
  { label: 'Month',  value: 'month' },
];

function formatTime(seconds) {
  const mins = Math.round(seconds / 60);
  if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${mins}m`;
}

function getDateRange(range) {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const today = `${yyyy}-${mm}-${dd}`;

  if (range === 'today') {
    return { from: `${today}T00:00:00`, to: `${today}T23:59:59` };
  }
  if (range === 'week') {
    const d = new Date(now);
    d.setDate(d.getDate() - d.getDay());
    const wd = d.toISOString().slice(0, 10);
    return { from: `${wd}T00:00:00`, to: null };
  }
  // month
  return { from: `${yyyy}-${mm}-01T00:00:00`, to: null };
}

export default function Apps({ kidId, parentId, deviceFilter }) {
  const [data, setData] = useState([]);
  const [range, setRange] = useState('today');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!parentId) return;
    const load = async () => {
      setLoading(true);
      setProgress(20);
      const { from, to } = getDateRange(range);
      let q = supabase.from('app_usage').select('app_name, duration_seconds').eq('parent_id', parentId);
      if (kidId) q = q.eq('kid_id', kidId);
      if (deviceFilter && deviceFilter !== 'all') q = q.eq('source', deviceFilter);
      if (from) q = q.gte('timestamp', from);
      if (to) q = q.lte('timestamp', to);
      const { data: rows, error } = await q;
      if (error) { console.error('App usage error:', error); setLoading(false); return; }
      setProgress(70);

      const agg = {};
      (rows || []).forEach(r => {
        agg[r.app_name] = (agg[r.app_name] || 0) + r.duration_seconds;
      });

      const sorted = Object.entries(agg)
        .map(([name, sec]) => ({ name, seconds: sec }))
        .sort((a, b) => b.seconds - a.seconds)
        .slice(0, 10);

      setData(sorted);
      setProgress(100);
      setLoading(false);
    };
    load();
  }, [range, parentId, kidId, deviceFilter]);

  const totalSeconds = data.reduce((s, r) => s + r.seconds, 0);
  const totalMin = Math.round(totalSeconds / 60);

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">App Usage</h1>
        <div className="filter-bar">
          {RANGES.map(r => (
            <button
              key={r.value}
              className={`filter-btn ${range === r.value ? 'active' : ''}`}
              onClick={() => setRange(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="stat-row">
        <div className="stat-card">
          <div className="stat-label">Total Screen Time</div>
          <div className="stat-value">{totalSeconds > 0 ? formatTime(totalSeconds) : '--'}</div>
          {totalMin > 180 && <div className="stat-sub" style={{ color: 'var(--danger)' }}>High usage</div>}
        </div>
        <div className="stat-card">
          <div className="stat-label">Top App</div>
          <div className="stat-value" style={{ fontSize: 18 }}>{data[0]?.name || '--'}</div>
          {data[0] && <div className="stat-sub">{formatTime(data[0].seconds)}</div>}
        </div>
        <div className="stat-card">
          <div className="stat-label">Apps Used</div>
          <div className="stat-value">{data.length}</div>
        </div>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-icon">{'\uD83D\uDCF1'}</div>
          <div className="loading-title">Loading app usage...</div>
          <div className="loading-bar-track">
            <div className="loading-bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="loading-percent">{progress}%</div>
        </div>
      )}

      {!loading && data.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <div className="icon">📱</div>
            <div className="title">No app usage data</div>
            <div className="desc">No data found for this time range. Make sure the agent is running.</div>
          </div>
        </div>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* Bar chart */}
          <div className="card">
            <div className="card-title">Top Apps (minutes)</div>
            <ResponsiveContainer width="100%" height={Math.max(250, data.length * 40)}>
              <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
                <XAxis
                  type="number"
                  tick={{ fontSize: 12 }}
                  tickFormatter={v => `${Math.round(v / 60)}m`}
                />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={120} />
                <Tooltip formatter={v => [formatTime(v), 'Usage']} />
                <Bar dataKey="seconds" radius={[0, 6, 6, 0]}>
                  {data.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="card">
            <div className="card-title">Breakdown</div>
            <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>App Name</th>
                  <th>Time Used</th>
                  <th>Share %</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const pct = totalSeconds ? Math.round((row.seconds / totalSeconds) * 100) : 0;
                  return (
                    <tr key={row.name}>
                      <td>
                        <span style={{ color: COLORS[i % COLORS.length], marginRight: 8 }}>&#9632;</span>
                        {row.name}
                      </td>
                      <td>{formatTime(row.seconds)}</td>
                      <td>{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
